import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const V = {
  ink:'#0c1830', ink2:'#2a3956', ink3:'#5a6680', ink4:'#8a93a6',
  paper:'#fbfaf6', paper2:'#f3f1ea', paper3:'#e6e2d5', line:'#d8d3c2',
  gold:'#c8a64b', good:'#2a6f4f', goodSoft:'#e8f0eb',
  bad:'#9c2b1a', badSoft:'#f4e8e5', warn:'#a8731a', warnSoft:'#f5ecd9',
  serif:"'Source Serif 4', Georgia, serif",
  sans:"'Inter', -apple-system, system-ui, sans-serif",
  mono:"'JetBrains Mono', ui-monospace, monospace",
};

const TYPE_COLORS = {
  'Contract':        { color: V.good, bg: '#D1FAE5', border: '#A7F3D0' },
  'Amendment':       { color: '#991B1B', bg: '#FEE2E2', border: '#FECACA' },
  'Proposal':        { color: V.warn, bg: '#FEF3C7', border: '#FDE68A' },
  'Quotation':       { color: '#1E3A8A', bg: '#DBEAFE', border: '#BFDBFE' },
  'BOQ':             { color: '#1E3A8A', bg: '#DBEAFE', border: '#BFDBFE' },
  'Variation Order': { color: '#6B21A8', bg: '#F3E8FF', border: '#E9D5FF' },
  'Purchase Order':  { color: V.warn, bg: '#FEF3C7', border: '#FDE68A' },
};

function Tag({ type }) {
  const st = TYPE_COLORS[type] || { color: V.ink3, bg: V.paper3, border: V.line };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: st.color, background: st.bg, border: `1px solid ${st.border}`, fontFamily: V.sans }}>
      {type || '—'}
    </span>
  );
}

function formatValue(v, cur = 'EUR') {
  if (!v) return '—';
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym}${Number(v).toLocaleString('en-GB')}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getStatus(doc) {
  if (doc.awarded) return { color: V.good, bg: '#D1FAE5', label: 'Awarded' };
  if (doc.validity_date && new Date(doc.validity_date) < new Date()) return { color: '#991B1B', bg: '#FEE2E2', label: 'Expired' };
  return { color: '#1E3A8A', bg: '#DBEAFE', label: 'Active' };
}

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── DELETE CONFIRM MODAL ────────────────────────────────────────────────────

function DeleteModal({ doc, onCancel, onConfirm, deleting }) {
  return (
    <div style={dm.overlay} onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={dm.modal}>
        <div style={dm.header}>
          <div style={dm.icon}>✕</div>
          <div>
            <div style={dm.title}>Delete Document</div>
            <div style={dm.sub}>This action cannot be undone</div>
          </div>
        </div>
        <div style={dm.body}>
          <div style={dm.docName}>{doc.original_name}</div>
          <div style={dm.docMeta}>{doc.supplier || '—'} · {formatDate(doc.document_date)}</div>
        </div>
        <div style={dm.footer}>
          <button style={dm.cancelBtn} onClick={onCancel} disabled={deleting}>Cancel</button>
          <button style={{ ...dm.deleteBtn, opacity: deleting ? 0.6 : 1 }} onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Document'}
          </button>
        </div>
      </div>
    </div>
  );
}

const dm = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:     { background: V.paper, borderRadius: 6, width: '100%', maxWidth: 400, boxShadow: '0 32px 64px rgba(12,24,48,0.22)', borderTop: `3px solid ${V.bad}`, overflow: 'hidden' },
  header:    { padding: '20px 24px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', gap: 14 },
  icon:      { width: 32, height: 32, borderRadius: '50%', background: V.badSoft, color: V.bad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  title:     { fontFamily: V.serif, fontSize: 16, fontWeight: 500, color: V.ink },
  sub:       { fontSize: 11, color: V.ink4, marginTop: 2, fontFamily: V.sans },
  body:      { padding: '16px 24px', background: V.paper2, borderBottom: `1px solid ${V.line}` },
  docName:   { fontSize: 13, fontWeight: 500, color: V.ink, fontFamily: V.mono },
  docMeta:   { fontSize: 11, color: V.ink4, marginTop: 4, fontFamily: V.sans },
  footer:    { padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  cancelBtn: { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '8px 18px', fontSize: 13, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },
  deleteBtn: { background: V.bad, border: 'none', borderRadius: 4, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: V.paper, cursor: 'pointer', fontFamily: V.sans },
};

// ─── SUPPLIER HELPERS ────────────────────────────────────────────────────────

function namesAreSimilar(a, b) {
  if (!a || !b) return false;
  const norm = str => str.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
  const na = norm(a), nb = norm(b);
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const wordsA = na.split(' ').filter(w => w.length > 2);
  const wordsB = nb.split(' ').filter(w => w.length > 2);
  const shared = wordsA.filter(w => wordsB.includes(w));
  return shared.length >= Math.min(2, Math.min(wordsA.length, wordsB.length));
}

const SUP_CATEGORIES = [
  { value: 'steel',      label: 'Steel'      },
  { value: 'bunkers',    label: 'Bunkers'    },
  { value: 'electrical', label: 'Electrical' },
  { value: 'civil',      label: 'Civil'      },
  { value: 'equipment',  label: 'Equipment'  },
  { value: 'services',   label: 'Services'   },
  { value: 'other',      label: 'Other'      },
];

// ─── SUPPLIER DROPDOWN ────────────────────────────────────────────────────────

function SupplierDropdown({ value, onChange, onNew }) {
  const [search, setSearch]   = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const ref                   = useRef(null);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/api/suppliers?is_active=1${search ? `&search=${encodeURIComponent(search)}` : ''}`)
        .then(res => setOptions(res.data.suppliers || []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        style={{ ...mu.input, display: 'flex', alignItems: 'center', cursor: 'pointer', background: open ? `${V.gold}20` : V.paper, borderColor: open ? V.gold : V.line }}
        onClick={() => setOpen(o => !o)}
      >
        {value ? (
          <>
            <span style={{ flex: 1, fontSize: 12, color: V.ink, fontFamily: V.sans }}>{value.name}</span>
            <span style={{ fontSize: 13, color: V.ink4, cursor: 'pointer', padding: '0 4px' }} onClick={e => { e.stopPropagation(); onChange(null); }}>×</span>
          </>
        ) : (
          <>
            {open
              ? <input autoFocus style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 12, color: V.ink, fontFamily: V.sans, outline: 'none', padding: 0 }} placeholder="Type to search registry…" value={search} onChange={e => { e.stopPropagation(); setSearch(e.target.value); }} onClick={e => e.stopPropagation()} />
              : <span style={{ flex: 1, fontSize: 12, color: V.ink4, fontFamily: V.sans }}>Select or search supplier…</span>
            }
            <span style={{ fontSize: 10, color: V.ink4 }}>▾</span>
          </>
        )}
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, background: V.paper, border: `1px solid ${V.gold}`, borderTop: 'none', borderRadius: '0 0 4px 4px', boxShadow: '0 8px 24px rgba(10,20,45,0.12)', maxHeight: 180, overflowY: 'auto' }}>
          {loading && <div style={{ padding: '9px 14px', fontSize: 12, color: V.ink4 }}>Loading…</div>}
          {!loading && options.length === 0 && <div style={{ padding: '9px 14px', fontSize: 12, color: V.ink4 }}>{search ? `No match for "${search}"` : 'No suppliers in registry'}</div>}
          {!loading && options.map(sup => (
            <div key={sup.id} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: V.ink, borderBottom: '1px solid #F3F4F6' }} onMouseEnter={e => e.currentTarget.style.background = V.paper} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} onClick={() => { onChange({ id: sup.id, name: sup.name }); setSearch(''); setOpen(false); }}>
              <div style={{ fontWeight: 600 }}>{sup.name}</div>
              <div style={{ fontSize: 10, color: V.ink4, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{sup.category} · {sup.country || '—'}</div>
            </div>
          ))}
          <div style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: V.gold, fontWeight: 600, borderTop: `1px solid ${V.line}` }} onMouseEnter={e => e.currentTarget.style.background = `${V.gold}20`} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} onClick={() => { setOpen(false); onNew(search); }}>
            + Register new supplier{search ? ` "${search}"` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── QUICK REGISTER SUPPLIER ──────────────────────────────────────────────────

function QuickRegisterSupplier({ initialName, initialCountry, initialCategory, onCreated, onCancel }) {
  const [form, setForm]     = useState({ name: initialName || '', category: initialCategory || 'other', country: initialCountry || '', contact_email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Name required.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await api.post('/api/suppliers', form);
      onCreated({ id: res.data.supplier.id, name: res.data.supplier.name });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #D97706', borderRadius: 4, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: V.warn }}>Register New Supplier</div>
        <button style={{ background: 'none', border: 'none', color: V.ink4, fontSize: 13, cursor: 'pointer' }} onClick={onCancel}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 10 }}>
        <div style={mu.field}>
          <label style={mu.label}>Company name *</label>
          <input style={mu.input} value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Supplier name" />
        </div>
        <div style={mu.field}>
          <label style={mu.label}>Category</label>
          <select style={mu.sel} value={form.category} onChange={e => setF('category', e.target.value)}>
            {SUP_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={mu.field}>
          <label style={mu.label}>Country</label>
          <input style={mu.input} value={form.country} onChange={e => setF('country', e.target.value)} placeholder="e.g. Greece" />
        </div>
        <div style={mu.field}>
          <label style={mu.label}>Contact email</label>
          <input style={mu.input} type="email" value={form.contact_email} onChange={e => setF('contact_email', e.target.value)} placeholder="contact@company.com" />
        </div>
      </div>
      {error && <div style={{ fontSize: 11, color: V.bad }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={mu.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
        <button style={{ ...mu.uploadBtn, opacity: saving ? 0.6 : 1, padding: '7px 16px', fontSize: 12 }} onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating…' : 'Create & link'}
        </button>
      </div>
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────

function UploadModal({ projectId, onClose, onSuccess }) {
  const [file, setFile]             = useState(null);
  const [dragging, setDragging]     = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError]           = useState(null);

  // Supplier state — registry-aware
  const [supplier, setSupplier]                   = useState(null);   // { id, name } | null
  const [supplierSuggestion, setSupplierSuggestion] = useState(null); // { name, country, category, matched }
  const [showQuickReg, setShowQuickReg]           = useState(false);
  const [quickRegName, setQuickRegName]           = useState('');
  const [quickRegCountry, setQuickRegCountry]     = useState('');
  const [quickRegCategory, setQuickRegCategory]   = useState('other');

  const [form, setForm] = useState({
    document_type: '', total_value: '', currency: 'EUR',
    document_date: '', validity_date: '', payment_terms: '',
    version_label: '', vessel_name: '', site_location: '', awarded: false,
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── File → AI extraction → registry match ────────────────────────────────
  const handleFile = async (f) => {
    const allowed = ['.pdf', '.docx', '.doc', '.xlsx', '.xls'];
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) { setError('Only PDF, Word, and Excel files are allowed.'); return; }

    setFile(f); setError(null);
    setExtracting(true);
    setSupplier(null);
    setSupplierSuggestion(null);
    setShowQuickReg(false);

    try {
      const fd = new FormData();
      fd.append('file', f);
      const token   = localStorage.getItem('clarix_token');
      const apiBase = process.env.REACT_APP_API_URL || '';

      const res  = await fetch(`${apiBase}/api/documents/extract-metadata`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      const meta = data.metadata || {};

      // Auto-fill form fields
      const validTypes = ['Quotation','Proposal','Contract','Amendment','BOQ','Purchase Order','Variation Order','Invoice'];
      if (meta.document_type && validTypes.includes(meta.document_type)) setF('document_type', meta.document_type);
      if (meta.total_value)    setF('total_value', String(meta.total_value));
      if (meta.currency && ['EUR','USD','GBP','AED'].includes(meta.currency)) setF('currency', meta.currency);
      if (meta.document_date)  setF('document_date', meta.document_date);
      if (meta.validity_date)  setF('validity_date', meta.validity_date);
      if (meta.payment_terms)  setF('payment_terms', meta.payment_terms);
      if (meta.vessel_name)    setF('vessel_name', meta.vessel_name);
      if (meta.site_location)  setF('site_location', meta.site_location);

      // Supplier matching
      if (meta.supplier) {
        const suggestion = {
          name:     meta.supplier,
          country:  meta.supplier_country  || '',
          category: meta.supplier_category || 'other',
          matched:  null,
        };
        try {
          const supRes    = await api.get(`/api/suppliers?search=${encodeURIComponent(meta.supplier)}&is_active=1`);
          const candidates = supRes.data.suppliers || [];
          if (candidates.length > 0 && namesAreSimilar(meta.supplier, candidates[0].name)) {
            suggestion.matched = { id: candidates[0].id, name: candidates[0].name };
            setSupplier({ id: candidates[0].id, name: candidates[0].name });
          }
        } catch { /* non-fatal */ }
        setSupplierSuggestion(suggestion);
      }
    } catch (err) {
      console.error('Extraction error:', err);
    } finally {
      setExtracting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!file) { setError('Please select a file.'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== false) fd.append(k, v);
      });
      if (supplier) {
        fd.append('supplier_id', supplier.id);
        fd.append('supplier', supplier.name);
      }
      const token   = localStorage.getItem('clarix_token');
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally { setUploading(false); }
  };

  // ── Supplier section UI ──────────────────────────────────────────────────
  const renderSupplierSection = () => {
    if (extracting) {
      return (
        <div style={{ padding: '9px 12px', background: V.paper, border: '1px solid #D1D5DB', borderRadius: 3, fontSize: 12, color: V.ink4, display: 'flex', alignItems: 'center', gap: 8, fontFamily: V.sans }}>
          <span style={{ color: V.gold }}>✦</span> AI reading document…
        </div>
      );
    }

    if (supplierSuggestion?.matched && !showQuickReg) {
      return (
        <div style={{ background: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: 3, padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: V.good, fontSize: 13 }}>✓</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: V.good, fontFamily: V.sans }}>{supplierSuggestion.matched.name}</div>
              <div style={{ fontSize: 10, color: V.good, opacity: 0.8 }}>Matched in registry — will be linked</div>
            </div>
          </div>
          <button style={{ background: 'none', border: '1px solid #A7F3D0', borderRadius: 3, padding: '3px 9px', fontSize: 11, color: V.good, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setSupplier(null); setSupplierSuggestion(null); }}>
            Change
          </button>
        </div>
      );
    }

    if (supplierSuggestion && !supplierSuggestion.matched && !showQuickReg) {
      return (
        <div style={{ background: '#FFFBEB', border: '1px solid #D97706', borderRadius: 3, padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: V.warn, fontFamily: V.sans }}>
              {supplierSuggestion.name}
              {supplierSuggestion.country && <span style={{ fontWeight: 400, marginLeft: 6 }}>· {supplierSuggestion.country}</span>}
            </div>
            <div style={{ fontSize: 10, color: '#B45309', marginTop: 1 }}>AI identified — not yet in your registry</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              style={{ background: V.gold, border: 'none', borderRadius: 3, padding: '4px 10px', fontSize: 11, color: V.paper, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
              onClick={() => { setQuickRegName(supplierSuggestion.name); setQuickRegCountry(supplierSuggestion.country); setQuickRegCategory(supplierSuggestion.category); setShowQuickReg(true); }}
            >
              Register & link
            </button>
            <button
              style={{ background: 'none', border: '1px solid #D97706', borderRadius: 3, padding: '4px 8px', fontSize: 11, color: V.warn, cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={() => setSupplierSuggestion(null)}
            >
              Skip
            </button>
          </div>
        </div>
      );
    }

    if (showQuickReg) {
      return (
        <QuickRegisterSupplier
          initialName={quickRegName}
          initialCountry={quickRegCountry}
          initialCategory={quickRegCategory}
          onCreated={(newSup) => { setSupplier(newSup); setShowQuickReg(false); setSupplierSuggestion({ ...supplierSuggestion, matched: newSup }); }}
          onCancel={() => setShowQuickReg(false)}
        />
      );
    }

    return (
      <SupplierDropdown
        value={supplier}
        onChange={setSupplier}
        onNew={(name) => { setQuickRegName(name); setQuickRegCountry(''); setQuickRegCategory('other'); setShowQuickReg(true); }}
      />
    );
  };

  return (
    <div style={mu.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={mu.modal}>
        <div style={mu.header}>
          <div>
            <div style={mu.modalTitle}>Upload Document</div>
            <div style={mu.modalSub}>PDF, Word, or Excel — max 50MB</div>
          </div>
          <button style={mu.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={mu.body}>

          {/* Drop zone */}
          <div
            style={{ ...mu.dropzone, ...(dragging ? mu.dropzoneDrag : {}), ...(file ? mu.dropzoneDone : {}) }}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !extracting && document.getElementById('clarix-file-input').click()}
          >
            <input id="clarix-file-input" type="file" accept=".pdf,.docx,.doc,.xlsx,.xls" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
            {file ? (
              <>
                <div style={{ fontSize: 22 }}>📄</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: V.ink }}>{file.name}</div>
                {extracting
                  ? <div style={{ fontSize: 11, color: V.gold, fontWeight: 500 }}>✦ AI reading document — filling fields…</div>
                  : <div style={{ fontSize: 11, color: V.ink4 }}>{(file.size / 1024).toFixed(0)} KB · click to change</div>
                }
              </>
            ) : (
              <>
                <div style={{ fontSize: 22, color: V.ink4 }}>↑</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: V.ink3 }}>Drop file here or click to browse</div>
                <div style={{ fontSize: 11, color: V.ink4 }}>PDF, Word, Excel</div>
              </>
            )}
          </div>

          {/* Supplier — full width, registry-aware */}
          <div style={mu.field}>
            <label style={mu.label}>
              Supplier
              {extracting && <span style={{ color: V.gold, marginLeft: 6 }}>AI reading…</span>}
            </label>
            {renderSupplierSection()}
          </div>

          {/* Other fields grid — supplier removed */}
          <div style={{ ...mu.grid, gridTemplateColumns: showQuickReg ? '1fr' : '1fr 1fr' }}>
            {[
              { key: 'document_type', label: 'Document Type', type: 'select', opts: ['Proposal','Quotation','Contract','Amendment','Purchase Order','BOQ','Variation Order'] },
              { key: 'total_value',   label: 'Total Value',    type: 'number', ph: 'e.g. 380000' },
              { key: 'currency',      label: 'Currency',       type: 'cselect', opts: ['EUR','USD','GBP'] },
              { key: 'document_date', label: 'Document Date',  type: 'date' },
              { key: 'validity_date', label: 'Validity Date',  type: 'date' },
              { key: 'payment_terms', label: 'Payment Terms',  type: 'text', ph: 'e.g. Net 30' },
              { key: 'version_label', label: 'Version Label',  type: 'text', ph: 'e.g. v1, Final' },
              { key: 'vessel_name',   label: 'Vessel Name',    type: 'text', ph: 'e.g. MV Poseidon', note: 'maritime' },
              { key: 'site_location', label: 'Site Location',  type: 'text', ph: 'e.g. Athens Ring Road', note: 'construction' },
            ].map(f => (
              <div key={f.key} style={mu.field}>
                <label style={mu.label}>
                  {f.label}
                  {f.note && <span style={{ color: V.ink4, fontWeight: 400, marginLeft: 4 }}>({f.note})</span>}
                </label>
                {f.type === 'select' ? (
                  <select style={mu.sel} value={form[f.key]} onChange={e => setF(f.key, e.target.value)}>
                    <option value="">— Select —</option>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'cselect' ? (
                  <select style={mu.sel} value={form[f.key]} onChange={e => setF(f.key, e.target.value)}>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={mu.input} type={f.type} placeholder={f.ph} value={form[f.key]} onChange={e => setF(f.key, e.target.value)} />
                )}
              </div>
            ))}
          </div>

          {/* Awarded toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 20, borderRadius: 10, background: form.awarded ? V.gold : V.line, cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }} onClick={() => setF('awarded', !form.awarded)}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: V.paper, position: 'absolute', top: 3, left: form.awarded ? 19 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
            </div>
            <span style={{ fontSize: 12, color: form.awarded ? V.gold : V.ink3, fontWeight: form.awarded ? 600 : 400 }}>
              Mark as Awarded Contract
            </span>
          </div>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 3, padding: '10px 14px', fontSize: 12, color: V.bad }}>{error}</div>
          )}
        </div>
        <div style={mu.footer}>
          {file && !supplier && !showQuickReg && (
            <div style={{ flex: 1, fontSize: 11, color: '#a8731a', fontFamily: "'Inter', sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠</span> No supplier selected — line item extraction will be skipped
            </div>
          )}
          <button style={mu.cancelBtn} onClick={onClose} disabled={uploading}>Cancel</button>
          <button style={{ ...mu.uploadBtn, opacity: (uploading || extracting) ? 0.6 : 1 }} onClick={handleSubmit} disabled={uploading || extracting}>
            {uploading ? 'Uploading…' : extracting ? '✦ Reading document…' : '↑ Upload Document'}
          </button>
        </div>
      </div>
    </div>
  );
}

const mu = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(12,24,48,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:        { background: V.paper, borderRadius: 6, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 64px rgba(12,24,48,0.22)', borderTop: `3px solid ${V.gold}` },
  header:       { padding: '22px 26px 18px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 },
  modalTitle:   { fontFamily: V.serif, fontSize: 20, fontWeight: 500, color: V.ink },
  modalSub:     { fontSize: 12, color: V.ink4, marginTop: 3, fontFamily: V.sans },
  closeBtn:     { background: 'none', border: 'none', color: V.ink4, fontSize: 16, cursor: 'pointer', padding: 4 },
  body:         { padding: '22px 26px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18, flex: 1, background: V.paper2 },
  dropzone:     { border: `1.5px dashed ${V.line}`, borderRadius: 4, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', transition: 'all 0.15s', background: V.paper },
  dropzoneDrag: { borderColor: V.gold, background: '#fdf8ee' },
  dropzoneDone: { borderColor: V.gold, background: '#fdf8ee', borderStyle: 'solid' },
  grid:         { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field:        { display: 'flex', flexDirection: 'column', gap: 5 },
  label:        { fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink3, fontFamily: V.sans },
  input:        { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: '100%', boxSizing: 'border-box' },
  sel:          { background: V.paper, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 12px', fontSize: 13, color: V.ink, fontFamily: V.sans, outline: 'none', width: '100%', boxSizing: 'border-box' },
  footer:       { padding: '16px 26px', borderTop: `1px solid ${V.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: V.paper, flexShrink: 0 },
  cancelBtn:    { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 18px', fontSize: 13, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },
  uploadBtn:    { background: V.gold, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 600, color: V.ink, cursor: 'pointer', fontFamily: V.sans },
};

// ─── PULL FROM REGISTRY PANEL ────────────────────────────────────────────────
// Shown as a full-width panel below the filter bar when "Pull from registry"
// is clicked. Searches all documents across all projects for this client.

function PullFromRegistryPanel({ projectId, onClose, onLinked }) {
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filters, setFilters]     = useState({ supplier_id: '', doc_type: '', year: '', commodity: '' });
  const [filterOpts, setFilterOpts] = useState({ suppliers: [], docTypes: [], years: [] });
  const [inputVal, setInputVal]   = useState('');
  const [selected, setSelected]   = useState(new Set());
  const [linking, setLinking]     = useState(false);
  const [error, setError]         = useState(null);
  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const load = (f = filters) => {
    setLoading(true);
    const params = new URLSearchParams({ exclude_project_id: projectId });
    if (f.supplier_id) params.set('supplier_id', f.supplier_id);
    if (f.doc_type)    params.set('doc_type',    f.doc_type);
    if (f.year)        params.set('year',         f.year);
    if (f.commodity)   params.set('commodity',    f.commodity);
    api.get(`/api/documents/registry?${params}`)
      .then(r => {
        setDocs(r.data.documents || []);
        if (r.data.filters) setFilterOpts(r.data.filters);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = () => {
    const next = { ...filters, commodity: inputVal };
    setFilters(next);
    load(next);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLink = async () => {
    if (selected.size === 0) return;
    setLinking(true); setError(null);
    try {
      await Promise.all([...selected].map(id =>
        api.post(`/api/documents/${id}/link-project`, { project_id: projectId })
      ));
      onLinked(selected.size);
    } catch (err) {
      setError('Some documents could not be linked. Please try again.');
    } finally { setLinking(false); }
  };

  return (
    <div style={{ background: V.paper, border: `2px solid ${V.gold}`, borderRadius: 4, margin: '0 48px 16px', boxShadow: '0 4px 20px rgba(10,20,45,0.08)' }}>
      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `${V.gold}20` }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: V.gold, fontFamily: V.sans }}>Pull from registry</div>
          <div style={{ fontSize: 11, color: V.ink3, marginTop: 2 }}>Search all documents across all projects — select to add to this project</div>
        </div>
        <button style={{ background: 'none', border: 'none', color: V.ink4, fontSize: 16, cursor: 'pointer', padding: 4 }} onClick={onClose}>✕</button>
      </div>

      {/* Search filters */}
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${V.line}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={rp.sel} value={filters.supplier_id} onChange={e => { setF('supplier_id', e.target.value); load({ ...filters, supplier_id: e.target.value }); }}>
          <option value="">All suppliers</option>
          {filterOpts.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select style={rp.sel} value={filters.doc_type} onChange={e => { setF('doc_type', e.target.value); load({ ...filters, doc_type: e.target.value }); }}>
          <option value="">All types</option>
          {filterOpts.docTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={rp.sel} value={filters.year} onChange={e => { setF('year', e.target.value); load({ ...filters, year: e.target.value }); }}>
          <option value="">All years</option>
          {filterOpts.years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <input
          style={{ ...rp.sel, flex: 1, minWidth: 180 }}
          placeholder="Search commodity, product, filename…"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
        />
        <button style={rp.btnSearch} onClick={handleSearch}>Search</button>
        {(filters.supplier_id || filters.doc_type || filters.year || filters.commodity) && (
          <button style={rp.btnClear} onClick={() => { setFilters({ supplier_id:'', doc_type:'', year:'', commodity:'' }); setInputVal(''); load({ supplier_id:'', doc_type:'', year:'', commodity:'' }); }}>Clear</button>
        )}
      </div>

      {/* Results */}
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: V.ink4 }}>Searching registry…</div>
        ) : docs.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: V.ink4 }}>No documents found. Try different filters.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: V.paper }}>
                <th style={rp.th}></th>
                <th style={rp.th}>Document</th>
                <th style={rp.th}>Supplier</th>
                <th style={rp.th}>Type</th>
                <th style={rp.th}>Date</th>
                <th style={rp.th}>Value</th>
                <th style={rp.th}>From project</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => {
                const isSel    = selected.has(doc.id);
                const isLinked = doc.already_linked;
                return (
                  <tr
                    key={doc.id}
                    style={{ borderBottom: '1px solid #F3F4F6', background: isSel ? `${V.gold}20` : isLinked ? V.paper : V.paper, opacity: isLinked ? 0.6 : 1 }}
                    onClick={() => !isLinked && toggleSelect(doc.id)}
                  >
                    <td style={{ padding: '10px 14px', width: 36 }}>
                      <div style={{ width: 16, height: 16, border: `1.5px solid ${isSel ? V.gold : V.line}`, borderRadius: 3, background: isSel ? V.gold : V.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isLinked ? 'default' : 'pointer' }}>
                        {isSel && <span style={{ color: V.paper, fontSize: 9, fontWeight: 700 }}>✓</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, color: V.ink, maxWidth: 220 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_name}</div>
                      {isLinked && <div style={{ fontSize: 10, color: V.gold, marginTop: 2 }}>Already in this project</div>}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#4B5563' }}>{doc.supplier_name || doc.supplier || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><Tag type={doc.document_type} /></td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: V.ink3, whiteSpace: 'nowrap' }}>{doc.document_date ? doc.document_date.substring(0, 10) : '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: V.mono, color: V.gold, fontWeight: 500 }}>
                      {doc.total_value ? `${doc.currency === 'USD' ? '$' : '€'}${Number(doc.total_value).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: V.ink4 }}>{doc.project_name || <span style={{ fontStyle: 'italic' }}>No project</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 20px', borderTop: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: V.paper }}>
        <div style={{ fontSize: 12, color: V.ink3 }}>
          {selected.size > 0
            ? `${selected.size} document${selected.size !== 1 ? 's' : ''} selected`
            : `${docs.filter(d => !d.already_linked).length} available to add`
          }
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {error && <span style={{ fontSize: 11, color: V.bad }}>{error}</span>}
          <button style={rp.btnCancel} onClick={onClose}>Cancel</button>
          <button
            style={{ ...rp.btnAdd, opacity: (selected.size === 0 || linking) ? 0.5 : 1 }}
            onClick={handleLink}
            disabled={selected.size === 0 || linking}
          >
            {linking ? 'Adding…' : `Add ${selected.size > 0 ? selected.size : ''} to project`}
          </button>
        </div>
      </div>
    </div>
  );
}

const rp = {
  sel:       { background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '7px 10px', fontSize: 12, color: V.ink, fontFamily: V.sans, outline: 'none', cursor: 'pointer' },
  th:        { padding: '9px 14px', fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4, textAlign: 'left', borderBottom: `1px solid ${V.line}` },
  btnSearch: { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans },
  btnClear:  { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '6px 12px', fontSize: 11, color: V.ink4, cursor: 'pointer' },
  btnCancel: { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '7px 16px', fontSize: 13, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },
  btnAdd:    { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans },
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function DocumentLibrary() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject]         = useState(null);
  const [documents, setDocuments]     = useState([]);
  const [filterOpts, setFilterOpts]   = useState({ suppliers: [], types: [], years: [] });
  const [filters, setFilters]         = useState({ type: '', supplier: '', year: '', search: '' });
  const [selected, setSelected]       = useState(new Set());
  const [loading, setLoading]         = useState(true);
  const [hoveredRow, setHoveredRow]   = useState(null);
  const [generating, setGenerating]   = useState(null);
  const [reportError, setReportError] = useState(null);
  const [uploadOpen, setUploadOpen]     = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [deleteDoc, setDeleteDoc]       = useState(null);
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => {
    if (projectId) {
      api.get(`/api/projects/${projectId}`)
        .then(r => setProject(r.data))
        .catch(console.error);
    }
  }, [projectId]);

  const loadDocuments = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (projectId)        params.set('project_id',    projectId);
    if (filters.type)     params.set('document_type', filters.type);
    if (filters.supplier) params.set('supplier',      filters.supplier);
    if (filters.year)     params.set('year',          filters.year);

    api.get(`/api/documents?${params}`)
      .then(r => {
        setDocuments(r.data.documents);
        setFilterOpts(r.data.filters || { suppliers: [], types: [], years: [] });
        setSelected(new Set());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId, filters.type, filters.supplier, filters.year]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      if (next.size >= 5) return prev;
      next.add(id); return next;
    });
  };

  const setFilter    = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const resetFilters = () => setFilters({ type: '', supplier: '', year: '', search: '' });
  const hasFilters   = filters.type || filters.supplier || filters.year || filters.search;

  const visible = filters.search.trim()
    ? documents.filter(d =>
        (d.original_name || '').toLowerCase().includes(filters.search.toLowerCase()) ||
        (d.supplier || '').toLowerCase().includes(filters.search.toLowerCase())
      )
    : documents;

  const selectedDocs = documents.filter(d => selected.has(d.id));

  const handleCompare = () => {
    navigate(`/projects/${projectId}/compare?docs=${[...selected].join(',')}`);
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await api.delete(`/api/documents/${deleteDoc.id}`);
      setDeleteDoc(null);
      setSelected(prev => { const next = new Set(prev); next.delete(deleteDoc.id); return next; });
      loadDocuments();
    } catch (err) {
      console.error('Delete error:', err);
    } finally { setDeleting(false); }
  };

  const handleGenerateReport = async (type) => {
    if (selected.size < 2) return;
    setGenerating(type); setReportError(null);
    try {
      const res = await api.post('/api/reports/generate', {
        project_id: parseInt(projectId), document_ids: Array.from(selected), type
      });
      const token   = localStorage.getItem('clarix_token');
      const apiBase = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBase}/api/reports/download/${encodeURIComponent(res.data.filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = res.data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setReportError('Failed to generate report. Please try again.');
    } finally { setGenerating(null); }
  };

  const { user, logout } = useAuth();
  const [previewDoc, setPreviewDoc] = useState(null);
  const docToken = localStorage.getItem('clarix_token') || '';
  const docAPI   = process.env.REACT_APP_API_URL || '';

  return (
    <div style={s.wrap}>
      <FontLoader />

      {/* Util bar */}
      <div style={s.utilBar}>
        <div style={s.utilDot}></div>
        <span style={s.utilLabel}>EVIDENCE LOCKER</span>
        <span style={{ fontSize: 11, color: 'rgba(251,250,246,0.45)', fontFamily: V.sans }}>{project?.name || ''}</span>
      </div>

      {/* Nav */}
      <div style={s.nav}>
        <div style={s.wordmark} onClick={() => navigate('/')}><span style={{ color: V.gold }}>V</span>erdict</div>
        <div style={s.navDivider}></div>
        <div style={s.navTag}>Negotiation Intelligence</div>
        <div style={{ display: 'flex', height: 60, alignItems: 'center' }}>
          {['Negotiations','The Room','Suppliers','Documents','Reports'].map(item => (
            <div key={item} style={{ padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', height: 60, display: 'flex', alignItems: 'center', color: V.ink3 }}
              onClick={() => { if (item === 'Negotiations') navigate('/'); if (item === 'Suppliers') navigate('/suppliers'); }}>
              {item}
            </div>
          ))}
        </div>
        <div style={s.navRight}>
          {user && <span style={s.navUser}>{user.email}</span>}
          {logout && <button style={s.navSignOut} onClick={logout}>Sign out</button>}
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={s.breadBar}>
        <span style={s.breadLink} onClick={() => navigate('/')}>Negotiations</span>
        <span style={s.breadSep}>/</span>
        {project && <span style={s.breadLink} onClick={() => navigate(`/projects/${projectId}`)}>{project.name}</span>}
        {project && <span style={s.breadSep}>/</span>}
        <span style={s.breadCurrent}>Evidence Locker</span>
      </div>

      {/* Page header */}
      <div style={s.pageTop}>
        <div>
          <div style={s.pageEyebrow}>Evidence Locker</div>
          <div style={s.pageTitle}>{visible.length} document{visible.length !== 1 ? 's' : ''} on file.</div>
          <div style={s.pageSub}>Every quotation, amendment, and award document — every line item indexed and searchable.</div>
        </div>
        <div style={s.btnGroup}>
          {selected.size > 0 && <button style={s.btnGhost} onClick={() => setSelected(new Set())}>✕ Clear</button>}
          <button style={s.btnGhost} onClick={() => navigate(`/projects/${projectId}`)}>← Dashboard</button>
          <button style={{ ...s.btnGhost, color: V.gold, borderColor: V.gold }} onClick={() => setRegistryOpen(r => !r)}>
            {registryOpen ? '✕ Close registry' : '⊕ Pull from registry'}
          </button>
          <button style={s.btnPrimary} onClick={() => setUploadOpen(true)}>↑ Upload</button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <div style={s.searchBox}>
          <span style={{ color: V.ink4 }}>⌕</span>
          <input style={s.searchInput} placeholder="Search filename, supplier, line item…" value={filters.search} onChange={e => setFilter('search', e.target.value)} />
          {filters.search && <span style={s.clearX} onClick={() => setFilter('search', '')}>✕</span>}
        </div>
        <select style={s.sel} value={filters.type}     onChange={e => setFilter('type', e.target.value)}>
          <option value="">All Types</option>
          {filterOpts.types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={s.sel} value={filters.supplier} onChange={e => setFilter('supplier', e.target.value)}>
          <option value="">Supplier: any</option>
          {filterOpts.suppliers.map(sup => <option key={sup} value={sup}>{sup}</option>)}
        </select>
        <select style={s.sel} value={filters.year}     onChange={e => setFilter('year', e.target.value)}>
          <option value="">Date: any</option>
          {filterOpts.years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {hasFilters && <button style={s.resetBtn} onClick={resetFilters}>Reset</button>}
        <div style={{ ...s.compareBtn, ...(selected.size >= 2 ? s.compareBtnReady : {}) }} onClick={selected.size >= 2 ? handleCompare : undefined}>
          <div style={{ ...s.ccount, ...(selected.size >= 2 ? s.ccountReady : {}) }}>{selected.size}</div>
          Compare Selected
        </div>
      </div>

      {/* Pull from registry panel */}
      {registryOpen && (
        <PullFromRegistryPanel
          projectId={projectId}
          onClose={() => setRegistryOpen(false)}
          onLinked={(count) => { setRegistryOpen(false); loadDocuments(); }}
        />
      )}

      {/* Table */}
      <div style={s.tableWrap}>
        {loading ? (
          <div style={s.stateMsg}>Loading documents…</div>
        ) : visible.length === 0 ? (
          <div style={s.stateMsg}>
            {hasFilters ? 'No documents match these filters.' : 'No documents yet — upload the first one.'}
            {hasFilters && <span style={s.stateLink} onClick={resetFilters}> Clear filters</span>}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: 40 }}></th>
                <th style={s.th}>Document</th>
                <th style={s.th}>Supplier</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Date</th>
                <th style={s.th}>Value</th>
                <th style={s.th}>Status</th>
                <th style={{ ...s.th, width: 40 }}>Flags</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(doc => {
                const isSel  = selected.has(doc.id);
                const status = getStatus(doc);
                const canSel = isSel || selected.size < 5;
                const isHov  = hoveredRow === doc.id;
                return (
                  <tr
                    key={doc.id}
                    style={{ ...s.tr, background: isSel ? `${V.gold}20` : isHov ? V.paper : V.paper, borderLeft: isSel ? `3px solid ${V.gold}` : '3px solid transparent' }}
                    onMouseEnter={() => setHoveredRow(doc.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td style={s.td} onClick={() => canSel && toggleSelect(doc.id)}>
                      <div style={{ ...s.chk, background: isSel ? V.gold : V.paper, borderColor: isSel ? V.gold : V.line, cursor: canSel ? 'pointer' : 'default' }}>
                        {isSel && <span style={{ color: V.paper, fontSize: 9, fontWeight: 700 }}>✓</span>}
                      </div>
                    </td>
                    <td style={{ ...s.td, ...s.tdMain, cursor: 'pointer' }} onClick={() => canSel && toggleSelect(doc.id)}>
                      {doc.original_name}
                      {doc.version_label && <span style={s.verLabel}>{doc.version_label}</span>}
                    </td>
                    <td style={s.td}>{doc.supplier_name || doc.supplier || '—'}</td>
                    <td style={s.td}><Tag type={doc.document_type} /></td>
                    <td style={s.td}>{formatDate(doc.document_date)}</td>
                    <td style={{ ...s.td, ...s.tdMono }}>{formatValue(doc.total_value, doc.currency)}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusPill, color: status.color, background: status.bg }}>{status.label}</span>
                    </td>
                    <td style={{ ...s.td, width: 64 }}>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <button
                          style={{ ...s.deleteBtn, background: V.paper3, color: V.ink3, opacity: isHov ? 1 : 0, pointerEvents: isHov ? 'auto' : 'none' }}
                          onClick={e => { e.stopPropagation(); setPreviewDoc({ id: doc.id, name: doc.original_name }); }}
                          title="Preview document"
                        >⊙</button>
                        <button
                          style={{ ...s.deleteBtn, opacity: isHov ? 1 : 0, pointerEvents: isHov ? 'auto' : 'none' }}
                          onClick={e => { e.stopPropagation(); setDeleteDoc(doc); }}
                          title="Delete document"
                        >✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Sticky compare bar */}
      {selected.size >= 2 && (
        <div style={s.compareBar}>
          <div style={s.cbarDocs}>
            {selectedDocs.map(d => (
              <div key={d.id} style={s.cbarDoc}>
                <span style={{ fontSize: 12, color: V.gold, fontWeight: 500 }}>{d.original_name}</span>
                <span style={s.cbarX} onClick={e => { e.stopPropagation(); toggleSelect(d.id); }}>✕</span>
              </div>
            ))}
          </div>
          <div style={s.cbarActions}>
            <span style={{ fontSize: 11, color: V.ink4 }}>{selected.size} selected</span>
            <button style={s.btnGhost} onClick={() => setSelected(new Set())}>Clear</button>
            <button style={s.btnPrimary} onClick={handleCompare}>⧉ Compare {selected.size} Documents</button>
            <button style={{ ...s.btnOutline, color: generating === 'word' ? V.ink4 : V.gold, borderColor: generating === 'word' ? V.line : V.gold, opacity: generating && generating !== 'word' ? 0.4 : 1 }} onClick={() => handleGenerateReport('word')} disabled={!!generating}>
              {generating === 'word' ? 'Generating…' : '↓ Word Report'}
            </button>
            <button style={{ ...s.btnOutline, color: generating === 'excel' ? V.ink4 : V.good, borderColor: generating === 'excel' ? V.line : V.good, opacity: generating && generating !== 'excel' ? 0.4 : 1 }} onClick={() => handleGenerateReport('excel')} disabled={!!generating}>
              {generating === 'excel' ? 'Generating…' : '↓ Excel Export'}
            </button>
            {reportError && <span style={{ fontSize: 11, color: V.bad }}>{reportError}</span>}
          </div>
        </div>
      )}

      {/* Modals */}
      {uploadOpen && <UploadModal projectId={projectId} onClose={() => setUploadOpen(false)} onSuccess={() => { setUploadOpen(false); loadDocuments(); }} />}
      {deleteDoc  && <DeleteModal doc={deleteDoc} onCancel={() => setDeleteDoc(null)} onConfirm={handleDelete} deleting={deleting} />}
      {previewDoc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(12,24,48,0.6)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setPreviewDoc(null)}>
          <div style={{ background:V.paper, borderRadius:6, width:'82vw', height:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(12,24,48,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', padding:'10px 14px', borderBottom:`1px solid ${V.line}`, background:V.paper2, flexShrink:0 }}>
              <span style={{ fontSize:12, fontFamily:V.mono, color:V.ink3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:12 }}>{previewDoc.name}</span>
              <a href={`${docAPI}/api/documents/${previewDoc.id}/file?token=${docToken}`} target="_blank" rel="noreferrer" style={{ fontSize:11, color:V.ink4, marginRight:16, textDecoration:'underline', textUnderlineOffset:2, whiteSpace:'nowrap', fontFamily:V.sans }}>Open in tab</a>
              <button onClick={() => setPreviewDoc(null)} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:V.ink3, padding:'0 2px', lineHeight:1 }}>✕</button>
            </div>
            <iframe src={`${docAPI}/api/documents/${previewDoc.id}/file?token=${docToken}`} style={{ flex:1, border:'none', width:'100%' }} title={previewDoc.name} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────

const s = {
  wrap:            { minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink, display: 'flex', flexDirection: 'column' },

  utilBar:         { background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12, flexShrink: 0 },
  utilDot:         { width: 6, height: 6, borderRadius: '50%', background: V.gold },
  utilLabel:       { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' },

  nav:             { background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', flexShrink: 0 },
  wordmark:        { fontFamily: V.serif, fontSize: 26, fontWeight: 600, letterSpacing: -0.5, color: V.ink, cursor: 'pointer' },
  navDivider:      { width: 1, height: 18, background: V.line, margin: '0 12px' },
  navTag:          { fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3, marginRight: 32, whiteSpace: 'nowrap' },
  navRight:        { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 },
  navUser:         { fontSize: 12, color: V.ink4 },
  navSignOut:      { background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer', fontFamily: V.sans },

  breadBar:        { background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', height: 52, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  breadLink:       { fontSize: 13, color: V.ink4, cursor: 'pointer', fontFamily: V.sans },
  breadSep:        { fontSize: 13, color: V.line },
  breadCurrent:    { fontSize: 13, color: V.ink, fontWeight: 500, fontFamily: V.sans },

  pageTop:         { padding: '28px 48px 22px', background: V.paper, borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexShrink: 0 },
  pageEyebrow:     { fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: V.ink4, marginBottom: 8, fontFamily: V.sans },
  pageTitle:       { fontFamily: V.serif, fontSize: 28, fontWeight: 400, color: V.ink, letterSpacing: -0.5, lineHeight: 1 },
  pageSub:         { fontSize: 12, color: V.ink4, marginTop: 6, fontFamily: V.sans },
  btnGroup:        { display: 'flex', gap: 10 },
  btnPrimary:      { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap' },
  btnGhost:        { background: 'none', color: V.ink, border: `1px solid ${V.line}`, borderRadius: 4, padding: '9px 16px', fontSize: 13, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap' },
  btnOutline:      { background: 'none', border: '1px solid', borderRadius: 4, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans },

  filterBar:       { padding: '10px 48px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', gap: 10, background: V.paper, flexShrink: 0, flexWrap: 'wrap' },
  searchBox:       { display: 'flex', alignItems: 'center', gap: 8, background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '7px 12px', minWidth: 220 },
  searchInput:     { background: 'none', border: 'none', outline: 'none', fontSize: 12, color: V.ink, fontFamily: V.sans, flex: 1 },
  clearX:          { color: V.ink4, cursor: 'pointer', fontSize: 10 },
  sel:             { background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, padding: '7px 10px', color: V.ink, fontSize: 12, fontFamily: V.sans, outline: 'none', cursor: 'pointer' },
  resetBtn:        { background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '6px 12px', color: V.ink4, fontSize: 12, cursor: 'pointer' },
  compareBtn:      { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', background: V.paper3, border: `1px solid ${V.line}`, borderRadius: 4, fontSize: 12, fontWeight: 600, color: V.ink4, whiteSpace: 'nowrap', fontFamily: V.sans },
  compareBtnReady: { background: '#fdf8ee', borderColor: V.gold, color: V.ink, cursor: 'pointer' },
  ccount:          { width: 20, height: 20, background: V.line, color: V.paper, borderRadius: '50%', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  ccountReady:     { background: V.gold, color: V.ink },

  tableWrap:       { flex: 1, overflowY: 'auto', padding: '16px 48px 120px', background: V.paper2 },
  table:           { width: '100%', borderCollapse: 'collapse', background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, overflow: 'hidden' },
  th:              { textAlign: 'left', padding: '11px 16px', fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4, borderBottom: `1px solid ${V.line}`, background: V.paper2 },
  tr:              { borderBottom: `0.5px solid ${V.line}`, transition: 'background 0.1s' },
  td:              { padding: '13px 16px', fontSize: 13, color: V.ink3, verticalAlign: 'middle', fontFamily: V.sans },
  tdMain:          { fontWeight: 500, color: V.ink, fontFamily: V.mono },
  tdMono:          { fontFamily: V.mono, fontSize: 12, color: V.ink2, fontWeight: 500 },
  chk:             { width: 16, height: 16, border: '1.5px solid', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' },
  verLabel:        { display: 'inline-block', marginLeft: 8, padding: '1px 6px', background: V.paper3, border: `1px solid ${V.line}`, borderRadius: 3, fontSize: 9, fontFamily: V.mono, color: V.ink4, textTransform: 'uppercase' },
  statusPill:      { display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  deleteBtn:       { background: V.badSoft, border: 'none', borderRadius: 3, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: V.bad, fontSize: 10, fontWeight: 700, transition: 'opacity 0.15s' },

  stateMsg:        { padding: '60px 0', textAlign: 'center', color: V.ink4, fontSize: 14, fontFamily: V.sans },
  stateLink:       { color: V.gold, cursor: 'pointer' },

  compareBar:      { position: 'fixed', bottom: 0, left: 0, right: 0, background: V.paper, borderTop: `2px solid ${V.gold}`, padding: '12px 48px', display: 'flex', alignItems: 'center', gap: 16, zIndex: 50, boxShadow: '0 -4px 20px rgba(12,24,48,0.10)' },
  cbarDocs:        { display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap' },
  cbarDoc:         { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#fdf8ee', border: `1px solid ${V.gold}`, borderRadius: 3 },
  cbarX:           { color: V.ink4, cursor: 'pointer', fontSize: 9, marginLeft: 2 },
  cbarActions:     { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
};
